/*
 * <product-fbt> — "Frequently bought together" product block
 * (snippets/product-fbt.liquid). Tick rows, pick a variant / quantity per row,
 * see the subtotal update, add everything ticked in one request. The cart
 * response is handed to cart-drawer / cart-notification (renderContents), the
 * same hand-off product-form.js uses.
 */
(function () {
  if (customElements.get('product-fbt')) return;

  // Shopify money formatting from shop.money_format (cents in, string out)
  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = (format || '${{amount}}').match(pattern);
    const key = match ? match[1] : 'amount';
    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    let out;
    switch (key) {
      case 'amount_no_decimals':
        out = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        out = withDelimiters(value, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        out = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        out = withDelimiters(value, 2, ',', '.');
    }
    return (format || '${{amount}}').replace(pattern, out);
  }

  class ProductFbt extends HTMLElement {
    constructor() {
      super();
      if (!(window.Shopify && Shopify.designMode) && /(^|; )inspd_s=[02](;|$)/.test(document.cookie)) {
        this.style.display = 'none';
        return;
      }
      this.rows = Array.from(this.querySelectorAll('[data-fbt-row]'));
      this.total = this.querySelector('[data-fbt-total]');
      this.compareTotal = this.querySelector('[data-fbt-compare-total]');
      this.count = this.querySelector('[data-fbt-count]');
      this.button = this.querySelector('[data-fbt-add]');
      this.spinner = this.button ? this.button.querySelector('.loading-overlay__spinner') : null;
      this.error = this.querySelector('.product-fbt__error');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      if (!this.rows.length || !this.button) return;

      this.rows.forEach((row) => {
        const check = row.querySelector('[data-fbt-check]');
        const variant = row.querySelector('[data-fbt-variant]');
        const qty = row.querySelector('[data-fbt-qty]');
        if (check) check.addEventListener('change', () => this.update());
        if (variant && variant.tagName === 'SELECT') {
          variant.addEventListener('change', () => {
            this.updateRowPrice(row);
            this.update();
          });
        }
        if (qty) qty.addEventListener('change', () => this.update());
        // clicking the pills must not toggle the row
        const controls = row.querySelector('.product-fbt__controls');
        if (controls) controls.addEventListener('click', (event) => event.stopPropagation());
      });

      this.button.addEventListener('click', () => this.add());
      this.update();
    }

    rowState(row) {
      const check = row.querySelector('[data-fbt-check]');
      const variant = row.querySelector('[data-fbt-variant]');
      const qty = row.querySelector('[data-fbt-qty]');
      let variantId = null;
      let price = 0;
      let compare = 0;
      if (variant) {
        if (variant.tagName === 'SELECT') {
          const option = variant.options[variant.selectedIndex];
          if (option) {
            variantId = option.value;
            price = Number(option.dataset.price) || 0;
            compare = Number(option.dataset.compare) || 0;
          }
        } else {
          variantId = variant.value;
          price = Number(variant.dataset.price) || 0;
          compare = Number(variant.dataset.compare) || 0;
        }
      }
      return {
        selected: !!(check && check.checked && !check.disabled && variantId),
        variantId,
        price,
        compare: compare > price ? compare : 0,
        quantity: qty ? Math.max(1, parseInt(qty.value, 10) || 1) : 1,
      };
    }

    updateRowPrice(row) {
      const state = this.rowState(row);
      const price = row.querySelector('[data-fbt-price]');
      const compare = row.querySelector('[data-fbt-compare]');
      if (price) price.textContent = formatMoney(state.price, this.moneyFormat);
      if (compare) {
        compare.hidden = !state.compare;
        if (state.compare) compare.textContent = formatMoney(state.compare, this.moneyFormat);
      }
    }

    update() {
      let total = 0;
      let compareTotal = 0;
      let items = 0;
      this.rows.forEach((row) => {
        const state = this.rowState(row);
        row.classList.toggle('is-selected', state.selected);
        if (!state.selected) return;
        total += state.price * state.quantity;
        compareTotal += (state.compare || state.price) * state.quantity;
        items += state.quantity;
      });
      if (this.total) this.total.textContent = formatMoney(total, this.moneyFormat);
      if (this.compareTotal) {
        const hasSaving = compareTotal > total;
        this.compareTotal.hidden = !hasSaving;
        if (hasSaving) this.compareTotal.textContent = formatMoney(compareTotal, this.moneyFormat);
      }
      if (this.count) this.count.textContent = items === 1 ? '1 item' : `${items} items`;
      this.button.setAttribute('aria-disabled', items === 0 ? 'true' : 'false');
      if (this.error) this.error.hidden = true;
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    add() {
      if (this.button.getAttribute('aria-disabled') === 'true' || this.button.classList.contains('loading')) return;
      const items = this.rows
        .map((row) => this.rowState(row))
        .filter((state) => state.selected)
        .map((state) => ({ id: Number(state.variantId), quantity: state.quantity }));
      if (!items.length) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      // same loading state as the product form's Add to cart button
      this.button.classList.add('loading');
      this.button.setAttribute('aria-busy', 'true');
      if (this.spinner) this.spinner.classList.remove('hidden');

      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartError, { source: 'product-fbt', errors: response.errors || response.description, message: response.message });
            }
            this.showError(response.description || response.message || 'Could not add to cart');
            return;
          }
          const added = response.items || [];
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-fbt', cartData: response });
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          // cart-notification needs a single line item key; use the last one added
          const last = added[added.length - 1] || {};
          cart.renderContents(Object.assign({}, response, { key: last.key, id: last.id }));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));
        })
        .catch((e) => {
          console.error(e);
          this.showError('Could not add to cart');
        })
        .finally(() => {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-busy');
          if (this.spinner) this.spinner.classList.add('hidden');
        });
    }
  }

  customElements.define('product-fbt', ProductFbt);
})();
